import React, { useState } from 'react';
import { GraduationCap, Package, ShoppingCart, Star, Clock, Award, Check, Zap, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { courses, libraries } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const MarketplacePage = () => {
  const { toast } = useToast();

  const handlePurchase = (item, type, isSubscription = false) => {
    const price = isSubscription ? item.subscriptionPrice : item.price;
    const priceType = isSubscription ? '/mes' : ' (único)';
    
    toast({
      title: '🛒 Próximamente',
      description: `La compra de ${item.title || item.name} estará disponible cuando integremos el sistema de pagos.`,
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0
    }).format(price);
  };

  const levelColors = {
    'Principiante': 'bg-green-500/10 text-green-400 border-green-500/20',
    'Intermedio': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    'Avanzado': 'bg-red-500/10 text-red-400 border-red-500/20'
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Header />
      
      <div className="flex-1 pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center mb-16 mt-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 text-transparent bg-clip-text">Marketplace</span> de Blox
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Acelera tu aprendizaje con cursos profesionales y expande tus capacidades con APIs avanzadas
            </p>
          </div>

          <Tabs defaultValue="libraries" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-slate-900 border border-slate-800 p-1">
                <TabsTrigger 
                  value="libraries" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-slate-950 text-slate-300 px-8"
                >
                  <Package className="w-4 h-4 mr-2" />
                  APIs / Librerías
                </TabsTrigger>
                <TabsTrigger 
                  value="courses" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-slate-950 text-slate-300 px-8"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Cursos
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Librerías */}
            <TabsContent value="libraries" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {libraries.map((library) => (
                  <Card key={library.id} className={`bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 ${library.isPack ? 'lg:col-span-2 border-cyan-500/30' : ''}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 ${library.isPack ? 'bg-gradient-to-br from-cyan-500/30 to-teal-500/30' : 'bg-gradient-to-br from-cyan-500/20 to-teal-500/20'} rounded-xl flex items-center justify-center`}>
                            {library.isPack ? <Zap className="w-6 h-6 text-cyan-400" /> : <Package className="w-6 h-6 text-cyan-400" />}
                          </div>
                          <div>
                            <CardTitle className="text-white text-xl">{library.name}</CardTitle>
                            <Badge className="mt-1 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 border text-xs">
                              {library.category}
                            </Badge>
                          </div>
                        </div>
                        {library.isPack && (
                          <Badge className="bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 border-0">
                            MEJOR VALOR
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-slate-400 mt-3">
                        {library.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                      {/* Features */}
                      <div className="mb-4">
                        <p className="text-sm text-slate-500 mb-2">Incluye:</p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {library.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                              <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Code Example */}
                      {library.codeExample && (
                        <div className="mb-4">
                          <p className="text-sm text-slate-500 mb-2">Ejemplo:</p>
                          <pre className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-cyan-300 overflow-x-auto">
                            <code>{library.codeExample}</code>
                          </pre>
                        </div>
                      )}

                      {/* Pricing */}
                      <div className="pt-4 border-t border-slate-800">
                        <div className="flex flex-col sm:flex-row gap-4">
                          {/* One-time purchase */}
                          <div className="flex-1 bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-500 mb-1">Compra única</p>
                            <p className="text-2xl font-bold text-white">{formatPrice(library.price)}</p>
                            <p className="text-xs text-slate-400 mt-1">Lleva la librería tal como está</p>
                          </div>
                          
                          {/* Subscription (if available) */}
                          {library.hasSubscription && (
                            <div className="flex-1 bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                              <p className="text-xs text-cyan-400 mb-1 flex items-center gap-1">
                                <RefreshCw className="w-3 h-3" /> Suscripción mensual
                              </p>
                              <p className="text-2xl font-bold text-white">{formatPrice(library.subscriptionPrice)}<span className="text-sm text-slate-400">/mes</span></p>
                              <p className="text-xs text-cyan-300 mt-1">Accede e influye en mejoras futuras</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                    
                    <CardFooter className="flex gap-3">
                      <Button 
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold"
                        onClick={() => handlePurchase(library, 'library', false)}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Comprar
                      </Button>
                      {library.hasSubscription && (
                        <Button 
                          className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold"
                          onClick={() => handlePurchase(library, 'library', true)}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Suscribirse
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Cursos */}
            <TabsContent value="courses" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {courses.map((course) => (
                  <Card key={course.id} className="bg-slate-900/50 border-slate-800 overflow-hidden relative">
                    {/* Coming Soon Overlay */}
                    {course.status === 'coming_soon' && (
                      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm z-10 flex items-center justify-center">
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-lg px-4 py-2">
                          <Clock className="w-5 h-5 mr-2" />
                          Próximamente
                        </Badge>
                      </div>
                    )}
                    
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={course.image} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Badge className={`${levelColors[course.level]} border`}>
                          {course.level}
                        </Badge>
                        <div className="flex items-center gap-1 text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm text-slate-400">4.8</span>
                        </div>
                      </div>
                      <CardTitle className="text-white text-xl">{course.title}</CardTitle>
                      <CardDescription className="text-slate-400 leading-relaxed">
                        {course.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Clock className="w-4 h-4 text-cyan-400" />
                          {course.duration}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Award className="w-4 h-4 text-cyan-400" />
                          Certificado incluido
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-800">
                        <div className="text-2xl font-bold text-white">
                          {formatPrice(course.price)}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold"
                        disabled={course.status === 'coming_soon'}
                        onClick={() => handlePurchase(course, 'course')}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {course.status === 'coming_soon' ? 'Próximamente' : 'Comprar Curso'}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Payment Info */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-semibold text-white mb-4">Métodos de Pago</h3>
              <p className="text-slate-400 mb-6">
                Próximamente aceptaremos pagos con <span className="text-cyan-400 font-semibold">Mastercard</span> y otras tarjetas
              </p>
              <div className="flex items-center justify-center gap-8 text-slate-500">
                <div className="text-sm">Tarjetas de Crédito</div>
                <div className="w-1 h-1 bg-slate-700 rounded-full"></div>
                <div className="text-sm">Tarjetas de Débito</div>
              </div>
              <p className="text-xs text-slate-500 mt-6">
                <span className="text-amber-400">Nota:</span> El sistema de pagos está en desarrollo. Contáctanos para compras anticipadas.
              </p>
              <a 
                href="mailto:blox.com.py@gmail.com" 
                className="inline-block mt-4 text-cyan-400 hover:text-cyan-300 text-sm"
              >
                blox.com.py@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MarketplacePage;
